import { CheckCircle2, Eye, EyeOff, Globe2, Lock, Mail, Phone, Store, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import BrandLogo from '../components/shared/BrandLogo';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import { useAuth } from '../hooks/useAuth';
import { COUNTRY_PRESETS } from '../lib/localization';
import { supabase } from '../lib/supabase';

const initialForm = {
  nome_oficina: '',
  responsavel: '',
  email: '',
  telefone: '',
  password: '',
  confirmPassword: '',
  countryCode: 'BR',
  website: '',
  acceptedTerms: false,
};

const countryLabels = {
  BR: 'Brasil',
  ES: 'España',
  PT: 'Portugal',
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  MX: 'México',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
};

const signupCopy = {
  pt: {
    title: 'Teste grátis por 30 dias | MecSync',
    badge: 'Teste grátis por 30 dias',
    headline: 'Sua oficina organizada ainda hoje.',
    intro: 'Crie seu acesso agora, sem cartão. Ao final do período, você escolhe se deseja continuar.',
    bullets: [
      'Acesso imediato ao sistema real',
      'Ordens de serviço, agenda, estoque e financeiro',
      'Plano Starter com os limites exibidos no site',
      'Pagamento somente se decidir continuar',
    ],
    formTitle: 'Criar meu acesso',
    formSubtitle: 'Leva menos de dois minutos.',
    referral: 'Indicação registrada',
    shopName: 'Nome da oficina',
    shopPlaceholder: 'Oficina do João',
    yourName: 'Seu nome',
    namePlaceholder: 'João Silva',
    email: 'E-mail de acesso',
    emailPlaceholder: 'voce@oficina.com.br',
    phone: 'WhatsApp',
    country: 'País da oficina',
    password: 'Senha',
    passwordPlaceholder: '8+ caracteres',
    confirmPassword: 'Confirmar senha',
    confirmPlaceholder: 'Repita a senha',
    showPassword: 'Mostrar senha',
    hidePassword: 'Ocultar senha',
    terms: 'Quero iniciar meu teste gratuito de 30 dias. Entendo que o acesso será direcionado ao pagamento quando o período terminar.',
    acceptError: 'Confirme que você aceita iniciar o período de teste.',
    passwordError: 'As senhas não coincidem.',
    createError: 'Não foi possível criar o teste grátis.',
    genericError: 'Não foi possível criar seu acesso. Tente novamente.',
    submitting: 'Criando acesso...',
    submit: 'Começar teste grátis',
    already: 'Já tem uma conta?',
    login: 'Entrar',
  },
  es: {
    title: 'Prueba gratis por 30 días | MecSync',
    badge: 'Prueba gratis por 30 días',
    headline: 'Tu taller organizado desde hoy.',
    intro: 'Crea tu acceso ahora, sin tarjeta. Al final del período, tú decides si deseas continuar.',
    bullets: [
      'Acceso inmediato al sistema real',
      'Órdenes, agenda, inventario y finanzas',
      'Plan Starter con los límites mostrados en el sitio',
      'Solo pagas si decides continuar',
    ],
    formTitle: 'Crear mi acceso',
    formSubtitle: 'Toma menos de dos minutos.',
    referral: 'Indicación registrada',
    shopName: 'Nombre del taller',
    shopPlaceholder: 'Taller Central',
    yourName: 'Tu nombre',
    namePlaceholder: 'Juan Silva',
    email: 'E-mail de acceso',
    emailPlaceholder: 'tu@taller.com',
    phone: 'WhatsApp',
    country: 'País del taller',
    password: 'Contraseña',
    passwordPlaceholder: '8+ caracteres',
    confirmPassword: 'Confirmar contraseña',
    confirmPlaceholder: 'Repite la contraseña',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    terms: 'Quiero iniciar mi prueba gratuita de 30 días. Entiendo que el acceso se dirigirá al pago cuando termine el período.',
    acceptError: 'Confirma que aceptas iniciar el período de prueba.',
    passwordError: 'Las contraseñas no coinciden.',
    createError: 'No fue posible crear la prueba gratis.',
    genericError: 'No fue posible crear tu acceso. Inténtalo nuevamente.',
    submitting: 'Creando acceso...',
    submit: 'Comenzar prueba gratis',
    already: '¿Ya tienes una cuenta?',
    login: 'Entrar',
  },
  en: {
    title: '30-day free trial | MecSync',
    badge: '30-day free trial',
    headline: 'Your shop organized today.',
    intro: 'Create your access now, no card required. At the end of the trial, you decide if you want to continue.',
    bullets: [
      'Immediate access to the real system',
      'Orders, schedule, inventory, and finance',
      'Starter plan with the limits shown on the site',
      'Pay only if you decide to continue',
    ],
    formTitle: 'Create my access',
    formSubtitle: 'Takes less than two minutes.',
    referral: 'Referral registered',
    shopName: 'Shop name',
    shopPlaceholder: 'Central Auto Shop',
    yourName: 'Your name',
    namePlaceholder: 'John Smith',
    email: 'Access email',
    emailPlaceholder: 'you@shop.com',
    phone: 'WhatsApp',
    country: 'Shop country',
    password: 'Password',
    passwordPlaceholder: '8+ characters',
    confirmPassword: 'Confirm password',
    confirmPlaceholder: 'Repeat password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    terms: 'I want to start my 30-day free trial. I understand access will move to payment when the period ends.',
    acceptError: 'Confirm that you accept starting the trial period.',
    passwordError: 'Passwords do not match.',
    createError: 'Could not create the free trial.',
    genericError: 'Could not create your access. Please try again.',
    submitting: 'Creating access...',
    submit: 'Start free trial',
    already: 'Already have an account?',
    login: 'Sign in',
  },
};

function countryCodeFromValue(value) {
  const countryCode = String(value || '').trim().toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  return COUNTRY_PRESETS[countryCode] ? countryCode : '';
}

function countryCodeFromBrowser() {
  const language = navigator.languages?.[0] || navigator.language || 'pt-BR';
  if (/^es-ES/i.test(language)) return 'ES';
  if (/^pt-PT/i.test(language)) return 'PT';
  if (/^es-MX/i.test(language)) return 'MX';
  if (/^es-AR/i.test(language)) return 'AR';
  if (/^es-CL/i.test(language)) return 'CL';
  if (/^es-CO/i.test(language)) return 'CO';
  if (/^en-GB/i.test(language)) return 'GB';
  if (/^en-CA/i.test(language)) return 'CA';
  if (/^en/i.test(language)) return 'US';
  if (/^es/i.test(language)) return 'ES';
  return 'BR';
}

function languageKeyFromCountry(countryCode) {
  const preset = COUNTRY_PRESETS[countryCode] || COUNTRY_PRESETS.BR;
  if (preset.language?.startsWith('es')) return 'es';
  if (preset.language?.startsWith('en')) return 'en';
  return 'pt';
}

function countryOptionLabel(code, preset) {
  return `${countryLabels[code] || code} - ${preset.currency}`;
}

export default function TrialSignup() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user, perfil, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const copy = useMemo(() => signupCopy[languageKeyFromCountry(form.countryCode)] || signupCopy.pt, [form.countryCode]);

  useEffect(() => {
    document.title = copy.title;
  }, [copy.title]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('ref') || params.get('parceiro') || params.get('partner') || '';
    const explicitCountry = countryCodeFromValue(params.get('country') || params.get('pais') || params.get('country_code') || '');
    const normalized = code
      .normalize('NFD')
      .replace(/[\u070-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);

    if (explicitCountry) {
      window.localStorage.setItem('mecsync_signup_country', explicitCountry);
      setForm((current) => ({ ...current, countryCode: explicitCountry }));
    } else {
      const storedCountry = countryCodeFromValue(window.localStorage.getItem('mecsync_signup_country') || '') || countryCodeFromBrowser();
      setForm((current) => ({ ...current, countryCode: storedCountry }));
    }

    if (normalized) {
      window.localStorage.setItem('mecsync_partner_ref', normalized);
      setReferralCode(normalized);
      return;
    }

    setReferralCode(window.localStorage.getItem('mecsync_partner_ref') || '');
  }, [location.search]);

  useEffect(() => {
    if (user && perfil) navigate('/dashboard', { replace: true });
  }, [navigate, perfil, user]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setError('');

    if (!form.acceptedTerms) {
      setError(copy.acceptError);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError(copy.passwordError);
      return;
    }

    // Password strength validation (must match Edge Function rules)
    if (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      setError('A senha deve ter ao menos 8 caracteres, com letras e números.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('create-trial-signup', {
        body: {
          nome_oficina: form.nome_oficina,
          responsavel: form.responsavel,
          email: form.email,
          telefone: form.telefone,
          password: form.password,
          country_code: form.countryCode,
          website: form.website,
          referral_code: referralCode,
        },
      });

      if (invokeError || data?.error) {
        throw new Error(data?.error || invokeError?.message || copy.createError);
      }

      await signIn(form.email.trim().toLowerCase(), form.password);
      navigate('/dashboard', { replace: true });
    } catch (submitError) {
      setError(submitError.message || copy.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#080809] px-4 py-8 text-white lg:py-12">
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <section className="flex flex-col justify-center rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/15 via-[#111113] to-[#111113] p-6 lg:p-10">
          <BrandLogo size="md" />
          <p className="mt-7 text-sm font-bold uppercase tracking-[0.18em] text-orange-400">{copy.badge}</p>
          <h1 className="mt-3 font-display text-4xl leading-tight">{copy.headline}</h1>
          <p className="mt-4 leading-7 text-zinc-70">{copy.intro}</p>

          <div className="mt-7 space-y-3 text-sm text-zinc-200">
            {copy.bullets.map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 shrink-0 text-orange-400" size={17} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <Card className="p-5 sm:p-7">
          <h2 className="font-display text-2xl">{copy.formTitle}</h2>
          <p className="mt-1 text-sm text-zinc-400">{copy.formSubtitle}</p>

          <div className="mt-4 rounded-lg border border-blue-500/20 bg-blue-500/10 px-3 py-2 text-sm text-blue-300">
            <strong>Aviso:</strong> Após o cadastro, você receberá um e-mail de boas-vindas com as instruções de acesso. Verifique sua caixa de entrada e spam.
          </div>

          {referralCode && (
            <div className="mt-4 rounded-lg border border-orange-400/7 bg-orange-500/10 px-3 py-2 text-sm text-orange-200">
              {copy.referral}: {referralCode}
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1.5 text-sm">
                <span>{copy.shopName}</span>
                <div className="relative">
                  <Store className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                  <Input className="pl-9" value={form.nome_oficina} onChange={(event) => updateField('nome_oficina', event.target.value)} placeholder={copy.shopPlaceholder} required minLength={2} maxLength={120} />
                </div>
              </label>

              <label className="space-y-1.5 text-sm">
                <span>{copy.yourName}</span>
                <div className="relative">
                  <UserRound className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                  <Input className="pl-9" value={form.responsavel} onChange={(event) => updateField('responsavel', event.target.value)} placeholder={copy.namePlaceholder} required minLength={2} maxLength={120} />
                </div>
              </label>
            </div>

            <label className="space-y-1.5 text-sm">
              <span>{copy.email}</span>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                <Input className="pl-9" type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} placeholder={copy.emailPlaceholder} required autoComplete="email" />
              </div>
            </label>

            <label className="space-y-1.5 text-sm">
              <span>{copy.phone}</span>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                <Input className="pl-9" mask="phone" value={form.telefone} onChange={(event) => updateField('telefone', event.target.value)} placeholder="+55 11 99999-9999" required />
              </div>
            </label>

            <label className="space-y-1.5 text-sm">
              <span>{copy.country}</span>
              <div className="relative">
                <Globe2 className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                <select
                  className="input-base w-full pl-9"
                  value={form.countryCode}
                  onChange={(event) => {
                    window.localStorage.setItem('mecsync_signup_country', event.target.value);
                    updateField('countryCode', event.target.value);
                  }}
                >
                  {Object.entries(COUNTRY_PRESETS).map(([code, preset]) => (
                    <option key={code} value={code}>
                      {countryOptionLabel(code, preset)}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1.5 text-sm">
                <span>{copy.password}</span>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                  <Input className="pl-9 pr-10" type={showPassword ? 'text' : 'password'} value={form.password} onChange={(event) => updateField('password', event.target.value)} placeholder={copy.passwordPlaceholder} required minLength={8} autoComplete="new-password" />
                  <button type="button" className="absolute right-3 top-2.5 text-zinc-400 hover:text-white" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? copy.hidePassword : copy.showPassword}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {form.password && (
                  <p className={`text-xs mt-1 ${form.password.length >= 8 && /[A-Za-z]/.test(form.password) && /\d/.test(form.password) ? 'text-green-400' : 'text-zinc-500'}`}>
                    {form.password.length >= 8 && /[A-Za-z]/.test(form.password) && /\d/.test(form.password) ? '✓ Senha válida' : 'Mínimo 8 caracteres, com letras e números'}
                  </p>
                )}
              </label>

              <label className="space-y-1.5 text-sm">
                <span>{copy.confirmPassword}</span>
                <Input type={showPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={(event) => updateField('confirmPassword', event.target.value)} placeholder={copy.confirmPlaceholder} required minLength={8} autoComplete="new-password" />
              </label>
            </div>

            <label className="hidden" aria-hidden="true">
              Website
              <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(event) => updateField('website', event.target.value)} />
            </label>

            <label className="flex items-start gap-2 text-sm text-zinc700">
              <input className="mt-1 accent-orange-500" type="checkbox" checked={form.acceptedTerms} onChange={(event) => updateField('acceptedTerms', event.target.checked)} />
              <span>{copy.terms}</span>
            </label>

            {error && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-70">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full py-3" disabled={submitting}>
              {submitting ? <><LoadingSpinner size="sm" /> {copy.submitting}</> : copy.submit}
            </Button>

            <p className="text-center text-sm text-zinc-400">
              {copy.already} <Link className="font-semibold text-orange-400 hover:text-orange-70" to="/login">{copy.login}</Link>
            </p>
          </form>
        </Card>
      </div>
    </main>
  );
}
